import type { FullPermission } from '../types/Role';
import type SinatraState from '../types/SinatraState';
import { selectUserOrganizations } from './selectUserOrganizations';

type UserState = SinatraState['user'];

/**
 * Find the permissions of the role matching appName for the current user.
 * @returns the role's permissions; [] when no role matches; undefined when the
 * user's profile is unavailable
 */
export function selectUserPermissionsByApp(
  user: UserState,
  appName: string,
): string[] | undefined {
  const organizations = selectUserOrganizations(user);
  if (!organizations) {
    return undefined;
  }
  let permissions: string[] = [];
  for (const org of organizations) {
    for (const role of org?.roles ?? []) {
      if (role?.application === appName) {
        permissions = role.permissions ?? [];
      }
    }
  }
  return permissions;
}

/**
 * Find the full permissions of the role matching appName for the current user.
 * @returns the role's permissions_full; [] when no role matches; undefined when
 * the user's profile is unavailable
 */
export function selectFullUserPermissionsByApp(
  user: UserState,
  appName: string,
): FullPermission[] | undefined {
  const organizations = selectUserOrganizations(user);
  if (!organizations) {
    return undefined;
  }
  let permissionsFull: FullPermission[] = [];
  for (const org of organizations) {
    for (const role of org?.roles ?? []) {
      if (role?.application === appName) {
        permissionsFull = role.permissions_full ?? [];
      }
    }
  }
  return permissionsFull;
}

const MEDICALERT = 'medicalert';
const PROFILE_INFO = 'profile_info';

/**
 * Normalize user permissions due to legacy naming conventions
 * @param permissions - User permissions
 */
function normalizeUserPermissions(permissions: string[]): string[] {
  return permissions.map(p => {
    if (p.toLowerCase() === PROFILE_INFO) {
      return MEDICALERT;
    }
    // TODO: return p.lowerCase() in future release to normalize inconsistency with capitaliziation
    return p;
  });
}

export default normalizeUserPermissions;
